import { Component, OnInit, ViewChild, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Location } from '@angular/common';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { Subject, Observable, of, race} from 'rxjs';
import { map } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { fadeInOut } from '../../../services/animations';
import { ReportDetailsViewModel, ReportService, BusinessService, ReportMetadataService, Int32StringTuple, StringStringTuple, MethodVersionService } from '../../index';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { ReportRequirementListComponent } from '../report-requirement-list/report-requirement-list.component';
import { AuthService } from '../../../services/auth.service';
import { ReportStatus } from '../../model/reportStatus';
import { ReportAspectGroupListComponent } from '../report-aspect-group-list/report-aspect-group-list.component';
import { ReportStandardGroupListComponent } from '../report-standard-group-list/report-standard-group-list.component';
import { Utilities } from '../../../services/utilities';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-report-details',
    templateUrl: './report-details.component.html',
    styleUrls: ['./report-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportDetailsComponent implements OnInit, OnChanges, CanComponentDeactivate {
    @ViewChild('form', { static: true }) form : NgForm;
    @ViewChild(ReportRequirementListComponent, { static: true }) requirementList: ReportRequirementListComponent;
    @ViewChild(ReportAspectGroupListComponent, { static: true }) aspectGroupList: ReportAspectGroupListComponent;
    @ViewChild(ReportStandardGroupListComponent, { static: true }) standardGroupList: ReportStandardGroupListComponent;
    @Input() businessId: number;

    fixedMethodVersionId: number = null;
    fixedBusinessCategoryId: number = null;

    report: ReportDetailsViewModel;
    businessCategories : Observable<Array<Int32StringTuple>>;
    businesses: Observable<Array<Int32StringTuple>>;
    reportStatuses: Observable<Array<Int32StringTuple>>;
    financiers: Observable<Array<Int32StringTuple>>;
    users: Observable<Array<StringStringTuple>>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;
    methodVersionName$: Observable<string>;
    hasPermissionToChange$: Observable<boolean> = of(false);
    reactivatePermission$: Observable<boolean> = of(false);

    id: number;
    reportForm: UntypedFormGroup;
    private onRecordSaved = new Subject<ReportDetailsViewModel>();
    reportSaved$ = this.onRecordSaved.asObservable();
    selectedTabIndex: number = 0;

    isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private reportService: ReportService,
        private reportMetadataService: ReportMetadataService,
        private businessService: BusinessService,
        private methodVersionService: MethodVersionService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
        ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('id'));

        // Check if specific method version is requested (for preview of new method versions)
        let mv = this.route.snapshot.queryParamMap.get('methodversionid');
        if (mv != null) {
            this.fixedMethodVersionId = Number.parseInt(mv);
        }
        let bc = this.route.snapshot.queryParamMap.get('businesscategoryid');
        if (bc != null) {
            this.fixedBusinessCategoryId = Number.parseInt(bc);
        }
    }


    ngOnInit() {
        if (!this.businessId) {
            this.businessId = Number.parseInt(this.route.snapshot.queryParamMap.get('businessid'));
        }

        this.buildForm();

        this.businesses = this.reportMetadataService.getBusinessMetadata();
        this.businessCategories = this.reportMetadataService.getBusinessCategoryMetadata();
        this.reportStatuses = this.reportMetadataService.getReportStatusMetadata();
        this.financiers = this.reportMetadataService.getFinancierMetadata();
        this.users = this.reportMetadataService.getUserMetadata();

        if (this.id) {
            this.loadData();
        } else {
            this.isNew = true;
            this.report = this.initializeEmptyObject();
            this.resetForm();
            this.hasPermissionToChange$ = of(this.authService.userHasPermission(Permission.changeReports));
        }
    }

    ngOnChanges(): void {
        if (this.report) {
            this.isNew = false;
        }
        else {
            this.isNew = true;
            this.report = this.initializeEmptyObject();
        }

        this.resetForm();
    }

    private loadData(): void {
        this.reportService.getReportsById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
    }

     dataLoaded(report: ReportDetailsViewModel) {
         this.report = report;

         this.refreshPermissions(report);

         this.isNew = false;
         this.resetForm();
    }

    private refreshPermissions(report: ReportDetailsViewModel) : void {
        // Determine change permission. A report in final state cannot be changed
        let changePermission = this.authService.userHasPermission(Permission.changeReports);
        this.hasPermissionToChange$ = of(changePermission && report.reportStatusId === ReportStatus.Ongoing);

        // Only admins can reactivate a report if it's in final state
        let reactivatePermission = this.authService.currentUserIsAtLeastAdministrator();
        this.reactivatePermission$ =
            of(changePermission && reactivatePermission && report.reportStatusId !== ReportStatus.Ongoing);
    }

    public getFormControl(name: string): AbstractControl {
        return this.reportForm.controls[name];
    }

    private buildForm() {
        this.reportForm = this.formBuilder.group({
            reportId: '',
            reportStatusId: ['', Validators.required],
            businessCategoryId: ['', Validators.required],
            businessId: ['', Validators.required],
            financierId: '',
            methodVersionId: '',
            name: ['', [Validators.required, Validators.maxLength(100)]],
            scorePercentage: '',
            maxScore: '',
            score: '',
            scoreLevelName: '',
            reportYear: '',
            visitDate: '',
            approvalDate: '',
            introduction: '',
            contactPerson: ['', Validators.maxLength(200)],
            ownedBy: ['', Validators.required],
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
            standardFulfilledPercentage: ''
        });
    }

    public resetForm() {
        if (!this.report) {
            this.isNew = true;
            this.report = this.initializeEmptyObject();
        }

        if (this.isNew) {
            if (!this.report.ownedBy) {
                this.report.ownedBy = this.authService.currentUser.id;
            }

            if (!this.report.reportStatusId) {
                this.report.reportStatusId = ReportStatus.Ongoing;
            }
            if (!this.report.businessId && this.businessId) {
                this.report.businessId = this.businessId;
                this.loadInitialBusinessData(this.businessId);
            }

            if (this.isPreview()) {
                this.report.methodVersionId = this.fixedMethodVersionId;
                this.report.businessCategoryId = this.fixedBusinessCategoryId;
            }
        }

        this.reportForm.reset({
            reportId: this.report.reportId || '',
            reportStatusId: this.report.reportStatusId || '',
            businessCategoryId: this.report.businessCategoryId || '',
            businessId: this.report.businessId || '',
            financierId: this.report.financierId || '',
            methodVersionId: this.report.methodVersionId || '',
            name: this.report.name || '',
            scorePercentage: this.report.scorePercentage || '',
            maxScore: this.report.maxScore || '',
            score: this.report.score || '',
            scoreLevelName: this.report.scoreLevelName || '',
            reportYear: this.report.reportYear || '',
            visitDate: this.report.visitDate || '',
            approvalDate: new DateFormatterPipe().transform(this.report.approvalDate, false) || '',
            introduction: this.report.introduction || '',
            contactPerson: this.report.contactPerson || '',
            ownedBy: this.report.ownedBy || '',
            createdBy: this.report.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.report.createdDate, true) || '',
            updatedBy: this.report.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.report.updatedDate, true) || '',
            standardFulfilledPercentage: this.report.standardFulfilledPercentage || '',
        });

        if (this.report.methodVersionId) {
            this.methodVersionName$ =
                this.reportMetadataService.getMethodVersionNameById(this.report.methodVersionId)
                .pipe(map(t => t.item2));
        }
        if (this.report.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.report.createdBy).pipe(map(t => t.item2));
        }
        if (this.report.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.report.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): ReportDetailsViewModel {
        const result = <ReportDetailsViewModel>{};

        return result;
    }

    private prepareSave(): ReportDetailsViewModel {
        const formModel = this.reportForm.value;

        return {
            reportId: this.isNew ?null : formModel.reportId,
            reportStatusId: formModel.reportStatusId,
            businessCategoryId: formModel.businessCategoryId,
            businessId: formModel.businessId,
            financierId: formModel.financierId,
            methodVersionId: formModel.methodVersionId,
            name: formModel.name,
            reportYear: formModel.reportYear,
            visitDate: formModel.visitDate,
            approvalDate: formModel.approvalDate,
            introduction: formModel.introduction,
            contactPerson: formModel.contactPerson,
            ownedBy: formModel.ownedBy,
        };
    }


    public save() {
        //if (!this.form.submitted) {
        //    // Causes validation to update.
        //    this.form.onSubmit(null);
        //    return;
        //}

        if (!this.reportForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const editedReport = this.prepareSave();

        if (this.isNew) {
            if (this.isPreview()) {
                // Preview mode
                this.reportService.createPreviewReport(editedReport).subscribe(
                    t => this.saveCompleted(t),
                    error => this.saveFailed(error));
            } else {
                this.reportService.createReport(editedReport).subscribe(
                    t => this.saveCompleted(t),
                    error => this.saveFailed(error));
            }
        }
        else {
            this.reportService.updateReport(editedReport.reportId, editedReport).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }


    public cancel() {
        this.location.back();
    }

    public isDirty(): boolean {
        return this.reportForm.dirty || (this.requirementList && this.requirementList.isDirty());
    }

    public isPreview(): boolean {
        return this.fixedMethodVersionId != null;
    }

    public getDirtyMessage(): string {
        let message: string = "";

        let reportDetailsDirty = this.reportForm.dirty;
        let requirementsDirty = this.requirementList && this.requirementList.isDirty();

        if (reportDetailsDirty) {
            if (!requirementsDirty) {
                message = "Rapportdetaljerna har ändrats. Vill du gå vidare ändå?";
            } else {
                message = "Rapportdetaljerna och kraven har ändrats. Vill du gå vidare ändå?";
            }
        } else {
            if (requirementsDirty) {
                message = "Kraven har ändrats. Vill du gå vidare ändå?";
            }
        }

        return message;
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.isDirty()) {
            let snackbar = this.snackBar.open(this.getDirtyMessage(),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return race(close, remain);

        } else {
            return of(true);
        }
    }


    private saveCompleted(report: ReportDetailsViewModel) {
        this.report = report;
        this.id = report.reportId;
        this.isNew = false;
        this.refreshPermissions(report);

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), report.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();

        this.onRecordSaved.next(this.report);
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    // Update business category and method version when a business is selected when inserting new data
    // This is however not applicable when testing new method versions
    public businessChanged(business: MatSelect) {
        if (this.isNew && business.value && !this.isPreview()) {
            this.loadInitialBusinessData(business.value);
        }
    }

    private loadInitialBusinessData(businessId: number): void {
        this.businessService.getBusinessById(businessId).subscribe(t => {
            var nameControl = this.getFormControl("name");
            if (nameControl.value === "") {
                nameControl.setValue(t.name);
            }
            this.reportForm.get("businessCategoryId").setValue(t.businessCategoryId);
            this.methodVersionName$ = this.reportMetadataService.getActiveMethodVersionByBusinessCategoryId(t.businessCategoryId)
                .pipe(map(t => t.item2));
        });
    }

    // Update method version when a business is selected when inserting new data
    public businessCategoryChanged(businessCategory: MatSelect) {
        if (this.isNew && businessCategory.value) {
            this.methodVersionName$ = this.reportMetadataService.getActiveMethodVersionByBusinessCategoryId(businessCategory.value)
                .pipe(map(t => t.item2));
        }
    }

    // Refresh request from child component
    public refreshRequested(): void {
        this.loadData();
    }

    public gotoSummaryView(): void {
        this.router.navigate([this.report.reportSummaryRoute, this.report.reportId]);
    }

    delete() : void {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            this.report.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));

                this.reportService.deleteReport(this.report.reportId)
                    .subscribe(results => {
                            this.alertService.stopLoadingMessage();
                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("commands.Delete"),
                                this.report.name + " " + this.translationService.getTranslation("globalTexts.deleted"),
                                MessageSeverity.success);

                            setTimeout(() => this.location.back(), 1000);
                        },
                        error => {
                            this.alertService.stopLoadingMessage();

                            let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                                ": " +
                                Utilities.getHttpResponseMessage(error);

                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("globalTexts.DeleteError"),
                                errorMessage,
                                MessageSeverity.error,
                                error);
                        });
            });
    }

    hasPermissionToDelete(): boolean {
        return this.authService.userHasPermission(Permission.deleteReports);
    }

    reactivate(): void {
        this.reportService.reactivateReport(this.id).subscribe(t => this.saveCompleted(t), error => this.saveFailed(error));
    }

}
